import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dishService } from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching dishes with caching
 */
export const useDishes = (schoolId) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['dishes', schoolId, language],
    queryFn: () => dishService.getDishes(schoolId, language),
    staleTime: 1000 * 60 * 15, // 15 minutes (increased from 5)
    cacheTime: 1000 * 60 * 60, // 60 minutes (cache for longer)
  });
};

/**
 * Custom hook for fetching a single dish with caching
 */
export const useDish = (dishId) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['dish', dishId, language],
    queryFn: () => dishService.getDish(dishId, language),
    enabled: !!dishId, // Only run if dishId exists
  });
};

/**
 * Custom hook for creating a dish with optimistic updates
 */
export const useCreateDish = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newDish) => dishService.createDish(newDish),
    onMutate: async (newDish) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['dishes', newDish.schoolId, newDish.language],
        exact: true, // Add exact: true to only cancel exact matches
      });

      // Snapshot current data
      const previousDishes = queryClient.getQueryData(['dishes', newDish.schoolId, newDish.language]);

      // Optimistically add new dish
      if (previousDishes) {
        const optimisticDish = {
          id: `temp-${Date.now()}`,
          school_id: newDish.schoolId,
          name: newDish.name || (newDish.translations && newDish.translations[0]?.name) || 'New Dish',
          created_at: new Date().toISOString(),
          tags: newDish.tags || [],
          // Add any other required fields
        };

        queryClient.setQueryData(
          ['dishes', newDish.schoolId, newDish.language],
          [...previousDishes, optimisticDish]
        );
      }

      return { previousDishes };
    },
    onError: (err, newDish, context) => {
      // Revert to previous state on error
      if (context?.previousDishes) {
        queryClient.setQueryData(
          ['dishes', newDish.schoolId, newDish.language],
          context.previousDishes
        );
      }
    },
    onSettled: (data, error, variables) => {
      // Only invalidate if the mutation was successful
      if (!error) {
        queryClient.invalidateQueries({
          queryKey: ['dishes', variables.schoolId, variables.language],
          exact: true, // Only invalidate this exact query
        });
      }
    },
  });
};

/**
 * Custom hook for updating a dish with optimistic updates
 */
export const useUpdateDish = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, schoolId, ...dishData }) => 
      dishService.updateDish(id, dishData),
    onMutate: async ({ id, schoolId, ...dishData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['dishes', schoolId],
      });
      await queryClient.cancelQueries({
        queryKey: ['dish', id],
      });

      // Snapshot current data
      const previousDishes = queryClient.getQueryData(['dishes', schoolId]);
      const previousDish = queryClient.getQueryData(['dish', id]);

      // Optimistically update dishes list
      if (previousDishes) {
        const updatedDishes = previousDishes.map(dish => 
          dish.id === id ? { ...dish, ...dishData } : dish
        );
        
        queryClient.setQueryData(['dishes', schoolId], updatedDishes);
      }

      // Optimistically update single dish
      if (previousDish) {
        queryClient.setQueryData(['dish', id], {
          ...previousDish,
          ...dishData,
        });
      }

      return { previousDishes, previousDish };
    },
    onError: (err, variables, context) => {
      // Revert to previous state on error
      if (context?.previousDishes) {
        queryClient.setQueryData(
          ['dishes', variables.schoolId],
          context.previousDishes
        );
      }
      
      if (context?.previousDish) {
        queryClient.setQueryData(
          ['dish', variables.id],
          context.previousDish
        );
      }
    },
    onSettled: (data, error, variables) => {
      const { i18n } = require('react-i18next');
      const language = i18n.language || variables.language || 'en';
      
      // Handle new completion checker response format
      if (!error && data?.queueUpdates) {
        console.log(`[Dishes] Processing queue updates for dish ${variables.id}:`, data.queueUpdates);
        
        // Update specific queues based on completion status
        if (data.queueUpdates.removeFromTranslations) {
          queryClient.setQueryData(['missingTranslations', variables.schoolId], (oldData) => 
            oldData?.filter(item => item.id !== variables.id) || []
          );
        }
        
        if (data.queueUpdates.removeFromNutrients) {
          queryClient.setQueryData(['missingNutrients', variables.schoolId], (oldData) => 
            oldData?.filter(item => item.id !== variables.id) || []
          );
        }
        
        if (data.queueUpdates.removeFromTags) {
          queryClient.setQueryData(['missingTags', variables.schoolId], (oldData) => 
            oldData?.filter(item => item.id !== variables.id) || []
          );
        }
        
        console.log(`[Dishes] Queue caches updated with completion status`);
      } else if (error) {
        console.error(`[Dishes] Update failed for dish ${variables.id}:`, error);
      }
      
      // Refresh data to ensure consistency with improved query key invalidation
      queryClient.invalidateQueries({
        queryKey: ['dishes', variables.schoolId, language],
        exact: true, // Only invalidate the exact query
      });
      
      queryClient.invalidateQueries({
        queryKey: ['dish', variables.id, language],
        exact: true, // Only invalidate the exact query
      });
    },
  });
};

/**
 * Custom hook for deleting a dish with optimistic updates
 */
export const useDeleteDish = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, schoolId }) => dishService.deleteDish(id),
    onMutate: async ({ id, schoolId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['dishes', schoolId],
      });

      // Snapshot current data
      const previousDishes = queryClient.getQueryData(['dishes', schoolId]);

      // Optimistically remove dish from list
      if (previousDishes) {
        queryClient.setQueryData(
          ['dishes', schoolId],
          previousDishes.filter(dish => dish.id !== id)
        );
      }

      return { previousDishes };
    },
    onError: (err, variables, context) => {
      // Revert to previous state on error
      if (context?.previousDishes) {
        queryClient.setQueryData(
          ['dishes', variables.schoolId],
          context.previousDishes
        );
      }
    },
    onSettled: (data, error, variables) => {
      // Refresh data to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['dishes', variables.schoolId],
      });
      // Also invalidate any specific dish cache
      queryClient.invalidateQueries({
        queryKey: ['dish', variables.id],
      });
    },
  });
}; 